import i18n from '@/i18n';

export interface FriendlyError {
  title: string;
  description: string;
}

interface FriendlyErrorInput {
  errorCode?: string | null;
  errorMessage?: string | null;
  details?: string | null;
  httpStatus?: number | null;
  raw?: unknown;
}

function safeParseJson(value: unknown): any {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

/**
 * Single choke point for error copy: converting it here translates every screen that shows a
 * failure (Logs, chat, assistant simulation) at once.
 *
 * Resolved at call time rather than returning keys, so the four call sites stay unchanged.
 * Consequence: an error already rendered into the simulation transcript keeps the language it
 * was produced in until the run is repeated.
 */
export function getFriendlyError(input: FriendlyErrorInput): FriendlyError {
  const t = i18n.getFixedT(null, 'common');
  const parsedRaw = safeParseJson(input.raw);
  const parsedDetails = safeParseJson(input.details);

  const code: string = String(
    input.errorCode ||
      parsedRaw?.code ||
      parsedRaw?.error?.code ||
      parsedRaw?.error?.type ||
      parsedDetails?.code ||
      parsedDetails?.error?.code ||
      parsedDetails?.error?.type ||
      ''
  ).toLowerCase();

  const messageRaw: string = String(
    input.errorMessage ||
      parsedRaw?.error ||
      parsedRaw?.message ||
      parsedRaw?.error?.message ||
      ''
  );

  const detailsRaw: string = String(
    input.details ||
      parsedRaw?.details ||
      parsedRaw?.error?.message ||
      parsedDetails?.error?.message ||
      parsedDetails?.message ||
      ''
  );

  const combined = `${messageRaw} ${detailsRaw}`.toLowerCase();

  const pair = (name: string): FriendlyError => ({
    title: t(`errors.${name}Title`),
    description: t(`errors.${name}Description`),
  });

  if (
    code.includes('insufficient_quota') ||
    combined.includes('exceeded your current quota') ||
    combined.includes('insufficient_quota')
  ) {
    return pair('quota');
  }

  if (
    code.includes('rate_limit') ||
    combined.includes('rate limit') ||
    input.httpStatus === 429
  ) {
    return pair('rateLimit');
  }

  if (
    code.includes('invalid_api_key') ||
    code.includes('openai_token_not_configured') ||
    code.includes('openai_token_missing') ||
    combined.includes('invalid api key') ||
    combined.includes('incorrect api key')
  ) {
    return pair('invalidKey');
  }

  if (code.includes('model_not_found') || combined.includes('model not found')) {
    return pair('modelNotFound');
  }

  if (code.includes('assistant_not_found')) {
    return pair('assistantNotFound');
  }

  if (
    code.includes('empty_ai_response') ||
    code.includes('empty_fallback_response') ||
    code.includes('empty_ai_followup_response')
  ) {
    return pair('emptyResponse');
  }

  if (code.includes('followup_and_fallback_failed')) {
    return pair('actionsFailed');
  }

  if (
    code.includes('db_assistant_fetch_error') ||
    code.includes('db_integration_fetch_error') ||
    code.includes('db_fetch_error')
  ) {
    return pair('database');
  }

  if (code.includes('text_required')) {
    return pair('emptyMessage');
  }

  if (code.includes('no_pending_messages')) {
    return pair('noPending');
  }

  if (code.includes('supabase_config_missing')) {
    return pair('serverConfig');
  }

  if (code.includes('method_not_allowed')) {
    return pair('method');
  }

  if (input.httpStatus === 401 || input.httpStatus === 403) {
    return pair('unauthorized');
  }

  if (input.httpStatus && input.httpStatus >= 500) {
    return pair('server');
  }

  if (messageRaw && messageRaw.toLowerCase() !== 'internal server error') {
    // The upstream message comes from OpenAI/Supabase in their own wording — there is nothing
    // to translate here, so only the fallback description is localized.
    return {
      title: messageRaw,
      description: detailsRaw || t('errors.unexpectedDescription'),
    };
  }

  return pair('fallback');
}

/**
 * Turns technical errors from `account_scheduled_messages.error_message`
 * (WAHA / Puppeteer / pg_net / cron) into a short, friendly line.
 * Returns `null` when there is no error.
 */
export function getFriendlyScheduledMessageError(
  raw: string | null | undefined,
): string | null {
  if (!raw) return null;
  const t = i18n.getFixedT(null, 'common');
  const text = String(raw);
  const lower = text.toLowerCase();

  const httpMatch = lower.match(/waha http\s*(\d{3})/);
  const httpStatus = httpMatch ? Number(httpMatch[1]) : null;

  if (
    lower.includes('target closed') ||
    lower.includes('targetcloseerror') ||
    lower.includes('protocol error') ||
    lower.includes('page closed') ||
    lower.includes('session closed')
  ) {
    return t('scheduledErrors.connectionDropped');
  }

  if (httpStatus === 401 || httpStatus === 403) {
    return t('scheduledErrors.disconnected');
  }

  if (httpStatus === 404 && lower.includes('session')) {
    return t('scheduledErrors.sessionNotFound');
  }

  if (httpStatus === 422 || httpStatus === 400) {
    return t('scheduledErrors.rejected');
  }

  if (httpStatus === 429) {
    return t('scheduledErrors.tooMany');
  }

  if (httpStatus && httpStatus >= 500) {
    return t('scheduledErrors.serverFailure');
  }

  if (lower.includes('pg_net falhou')) {
    return t('scheduledErrors.dispatchFailed');
  }

  if (lower.includes('timeout aguardando callback')) {
    return t('scheduledErrors.timeout');
  }

  if (lower.includes('instância whatsapp não encontrada')) {
    return t('scheduledErrors.notConnected');
  }

  if (lower.includes('waha api configuration missing')) {
    return t('scheduledErrors.notConfigured');
  }

  if (lower.includes('account_id') && lower.includes('obrigat')) {
    return t('scheduledErrors.incompleteData');
  }

  return t('scheduledErrors.generic');
}
